import React from "react";
import PropTypes from "prop-types";
import Icon from "../Icon/Icon";
import { getMessage } from "../../data/locales";
import { buildTopicNodeDomId } from "../../utils/studentNavPaths";
import { buildAccordionResourceRows } from "../../utils/buildAccordionResourceRows";
import { expandTemarioBlocksForStudentCards } from "../../utils/temarioRootFolders";

const nodeShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nombre: PropTypes.string,
  archivos: PropTypes.arrayOf(PropTypes.object),
  testRefs: PropTypes.arrayOf(PropTypes.object),
  hijos: PropTypes.arrayOf(PropTypes.object),
});

function pathsEqual(left, right) {
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
    return false;
  }
  return left.every((value, index) => value === right[index]);
}

function getRowActionLabel() {
  return getMessage("topic.viewContent");
}

function TopicAccordionRow({
  row,
  focusNavPath,
  onActivate,
  mastered = false,
  isActive = false,
  panel = null,
}) {
  const domId = buildTopicNodeDomId(row.navPath);
  const isFocused = pathsEqual(focusNavPath, row.navPath) || isActive;
  return (
    <div
      id={domId || undefined}
      className={`cds-test-topic-row${isFocused ? " is-nav-focused" : ""}${isActive ? " is-inline-open" : ""}`}
    >
      <div className="cds-test-topic-row-main">
        <div className="cds-test-topic-left">
          {mastered ? (
            <Icon name="checkCircle" className="cds-test-icon cds-test-status-mastered" />
          ) : (
            <div className="cds-test-status-dot" />
          )}
          <span className="cds-test-topic-name">{row.node?.nombre || "—"}</span>
        </div>
        <div className="cds-test-topic-right">
          <button
            type="button"
            className="cds-test-btn-outline"
            onClick={() => onActivate(row)}
          >
            {getRowActionLabel()}
          </button>
        </div>
      </div>
      {panel}
    </div>
  );
}

TopicAccordionRow.propTypes = {
  row: PropTypes.shape({
    rowKey: PropTypes.string.isRequired,
    navPath: PropTypes.arrayOf(PropTypes.number).isRequired,
    node: nodeShape.isRequired,
  }).isRequired,
  focusNavPath: PropTypes.arrayOf(PropTypes.number),
  onActivate: PropTypes.func.isRequired,
  mastered: PropTypes.bool,
  isActive: PropTypes.bool,
  panel: PropTypes.node,
};

export default function TopicViewerAccordion({
  blocks,
  expandedKeys = [],
  onToggle,
  focusNavPath,
  onActivateRow,
  isBlockCompleted,
  activeRowKey = null,
  renderRowPanel = null,
  keyPrefix = "",
}) {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return (
      <div className="empty-state">
        <p>No hay temas disponibles en esta sección.</p>
      </div>
    );
  }

  const openKeys = Array.isArray(expandedKeys) ? expandedKeys : [];
  const prefix = typeof keyPrefix === "string" ? keyPrefix : "";
  const visibleBlocks = expandTemarioBlocksForStudentCards(blocks)
    .map((card, cardIndex) => ({
      bloque: card.bloque,
      idx: card.sourceIndex,
      cardIndex,
      kind: card.kind,
      rows: buildAccordionResourceRows(card.bloque, card.sourceIndex).map((row) => ({
        ...row,
        rowKey: `${prefix}${row.rowKey}`,
      })),
    }))
    .filter((item) => item.rows.length > 0);

  if (visibleBlocks.length === 0) {
    return (
      <div className="empty-state">
        <p>No hay temas disponibles en esta sección.</p>
      </div>
    );
  }

  return (
    <div className="cds-test-grid topic-viewer-accordion">
      {visibleBlocks.map(({ bloque, idx, cardIndex, kind, rows }) => {
        const key = `${prefix}${idx}-${kind}`;
        const isExpanded = openKeys.includes(key);
        const total = rows.length;
        const master = rows.length > 0
          ? rows.filter(({ node }) => (
            typeof isBlockCompleted === "function" && node?.id
              ? isBlockCompleted(node.id)
              : false
          )).length
          : (typeof isBlockCompleted === "function" && bloque?.id && isBlockCompleted(bloque.id) ? 1 : 0);
        const percent = total > 0 ? (master / total) * 100 : 0;
        const blockFocused =
          Array.isArray(focusNavPath) && focusNavPath.length > 0 && focusNavPath[0] === idx;

        return (
          <div
            key={`${prefix}${idx}-${kind}-${bloque?.id ?? cardIndex}`}
            className={`cds-test-card${isExpanded ? " is-expanded" : ""}${blockFocused ? " is-nav-focused" : ""}`}
            data-testid={`temario-root-card-${cardIndex}`}
            id={prefix ? key : (kind === "files"
              ? (buildTopicNodeDomId([idx]) || undefined)
              : `temario-tests-${idx}`)}
          >
            <button
              type="button"
              className="cds-test-card-trigger"
              onClick={() => onToggle(key)}
              aria-expanded={isExpanded}
            >
              <div className="cds-test-card-info">
                <h2>{bloque?.nombre || "—"}</h2>
                <div className="progress-container-mini">
                  <div className="cds-test-progress-track">
                    <div className="cds-test-progress-fill" style={{ width: `${percent}%` }} />
                  </div>
                  <span className="cds-test-meta-text">
                    {master}/{total} {getMessage("topic.masteredLabel")}
                  </span>
                </div>
              </div>
              <div className="cds-test-icon-box">
                <Icon
                  name="chevronDown"
                  className={`cds-test-icon cds-test-chevron${isExpanded ? " rotated" : ""}`}
                />
              </div>
            </button>
            <div className={`cds-test-collapse${isExpanded ? " open" : ""}`}>
              <div className="cds-test-topic-list">
                {rows.length > 0 ? (
                  rows.map((row) => (
                    <TopicAccordionRow
                      key={row.rowKey}
                      row={row}
                      focusNavPath={focusNavPath}
                      onActivate={onActivateRow}
                      isActive={activeRowKey === row.rowKey}
                      mastered={
                        typeof isBlockCompleted === "function" && row.node?.id
                          ? isBlockCompleted(row.node.id)
                          : false
                      }
                      panel={
                        activeRowKey === row.rowKey && typeof renderRowPanel === "function"
                          ? renderRowPanel(row)
                          : null
                      }
                    />
                  ))
                ) : (
                  <p className="cds-test-empty-msg">{getMessage("topic.noContentInSection")}</p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

TopicViewerAccordion.propTypes = {
  blocks: PropTypes.arrayOf(nodeShape).isRequired,
  expandedKeys: PropTypes.arrayOf(PropTypes.string),
  onToggle: PropTypes.func.isRequired,
  focusNavPath: PropTypes.arrayOf(PropTypes.number),
  onActivateRow: PropTypes.func.isRequired,
  isBlockCompleted: PropTypes.func,
  activeRowKey: PropTypes.string,
  renderRowPanel: PropTypes.func,
  keyPrefix: PropTypes.string,
};
